import { useCallback, useEffect, useRef, useState } from "react"
import { api } from "../../../../../env"
import { errMessage } from "../../../../../utils/errors/errorHandler"

// ---------------------------------------------------------------------------
// Pure helpers (ported unchanged from the original QaReviewPanel). They are
// stateless given the current `chapters`, so the hook and the inline UI can
// share them.
// ---------------------------------------------------------------------------

export const opNoun = (it) => (it.itemType === "spark" ? "spark" : it.itemType === "chapter" ? "chapter" : "activity")
export const opLabel = (it) => {
    const op = it.operation ?? "edit"
    const noun = opNoun(it)
    return op === "add" ? `Add ${noun}` : op === "remove" ? `Remove ${noun}` : `Edit ${noun}`
}
export const opColor = (op) => (op === "add" ? "#1a7f44" : op === "remove" ? "#b3261e" : "#6750a4")

// An issue is auto-applicable when its operation targets something we can act on in one click:
// "add" needs only a valid chapter; "edit"/"remove" need an in-range item (chapter-level for edit).
export const canApply = (it, chapters, hasOnChange) => {
    if (!hasOnChange) return false
    const op = it.operation ?? "edit"
    if (op === "none") return false
    const ci = it.chapterIndex
    if (typeof ci !== "number" || ci < 0 || ci >= chapters.length) return false
    if (op === "add") return it.itemType === "activity" || it.itemType === "spark"
    if (it.itemType === "chapter") return op === "edit"
    const arr = (it.itemType === "activity" ? chapters[ci]?.activities : it.itemType === "spark" ? chapters[ci]?.sparks : null) ?? null
    return Array.isArray(arr) && typeof it.itemIndex === "number" && it.itemIndex >= 0 && it.itemIndex < arr.length
}

// Apply an accepted issue's operation to its chapter. "add" appends at the END (existing indices stay
// valid); "remove" filters the target out (which shifts later indices — the hook remaps them); "edit"
// splices in place.
export const mergeOperation = (chapter, it, revised) => {
    const op = it.operation ?? "edit"
    if (op === "add") {
        if (it.itemType === "activity") {
            const activity = { title: revised.title ?? "", description: revised.description ?? "" }
            return { ...chapter, activities: [...(chapter.activities ?? []), activity] }
        }
        if (it.itemType === "spark") {
            const spark = { name: revised.name ?? revised.displayName ?? "", displayName: revised.displayName ?? "", description: revised.description ?? "", content: revised.content ?? "" }
            return { ...chapter, sparks: [...(chapter.sparks ?? []), spark] }
        }
        return chapter
    }
    if (op === "remove") {
        if (it.itemType === "activity") return { ...chapter, activities: (chapter.activities ?? []).filter((_, idx) => idx !== it.itemIndex) }
        if (it.itemType === "spark") return { ...chapter, sparks: (chapter.sparks ?? []).filter((_, idx) => idx !== it.itemIndex) }
        return chapter
    }
    // edit (in place)
    if (it.itemType === "chapter") {
        return { ...chapter, title: revised.title ?? chapter.title, theme: revised.theme ?? chapter.theme, description: revised.description ?? chapter.description }
    }
    if (it.itemType === "activity") {
        return { ...chapter, activities: (chapter.activities ?? []).map((a, idx) => idx === it.itemIndex ? { ...a, title: revised.title ?? a.title, description: revised.description ?? a.description } : a) }
    }
    if (it.itemType === "spark") {
        return { ...chapter, sparks: (chapter.sparks ?? []).map((s, idx) => idx === it.itemIndex ? { ...s, name: revised.name ?? s.name, displayName: revised.displayName ?? s.displayName, description: revised.description ?? s.description, content: revised.content ?? s.content } : s) }
    }
    return chapter
}

const beforeFields = (chapter, it) => {
    if (it.itemType === "chapter") return { Title: chapter.title ?? "", Theme: chapter.theme ?? "", Description: chapter.description ?? "" }
    if (it.itemType === "activity") {
        const a = (chapter.activities ?? [])[it.itemIndex] ?? {}
        return { Title: a.title ?? "", Description: a.description ?? "" }
    }
    const s = (chapter.sparks ?? [])[it.itemIndex] ?? {}
    return { Title: s.displayName ?? "", Description: s.description ?? "", Transcript: s.content ?? "" }
}

const afterFields = (it, revised, before) => {
    if (it.itemType === "chapter") return { Title: revised.title ?? before.Title, Theme: revised.theme ?? before.Theme, Description: revised.description ?? before.Description }
    if (it.itemType === "activity") return { Title: revised.title ?? before.Title, Description: revised.description ?? before.Description }
    return { Title: revised.displayName ?? before.Title, Description: revised.description ?? before.Description, Transcript: revised.content ?? before.Transcript }
}

const blankFields = (it) => {
    if (it.itemType === "chapter") return { Title: "", Theme: "", Description: "" }
    if (it.itemType === "activity") return { Title: "", Description: "" }
    return { Title: "", Description: "", Transcript: "" }
}

// Build the { op, itemType, before, after } diff descriptor the inline UI renders. Computed lazily
// against the CURRENT chapter so it never relies on a stale snapshot.
const buildDiff = (it, chapter, revised) => {
    const op = it.operation ?? "edit"
    const before = beforeFields(chapter ?? {}, it)
    if (op === "remove") return { op, itemType: it.itemType, before, after: blankFields(it) }
    if (op === "add") return { op, itemType: it.itemType, before: blankFields(it), after: afterFields(it, revised, before) }
    return { op, itemType: it.itemType, before, after: afterFields(it, revised, before) }
}

// The assembled chapters, in the BuildChapterDto shape the QA/revise endpoints expect.
const chapterPayload = (chapters) => chapters.map(c => ({
    title: c.title,
    theme: c.theme,
    description: c.description,
    activities: (c.activities ?? []).map(a => ({ title: a.title, description: a.description })),
    sparks: (c.sparks ?? []).map(s => ({ name: s.name || s.displayName, displayName: s.displayName, description: s.description, content: s.content })),
}))

// Sibling chapter titles/themes, so a generated revision stays distinct from the rest of the track.
const outlineCtx = (chapters) => chapters.map(c => ({ title: c.title, theme: c.theme, description: c.description })).filter(c => c.title)

// ---------------------------------------------------------------------------
// useQaReview — owns the Cursor-style review queue.
//
// validate() fetches a QA report, then we build a QUEUE of the auto-applicable issues and walk them
// ONE AT A TIME. The active change's proposed content is fetched up front (or computed client-side
// for a remove) so its diff is shown BEFORE the author decides. Accept applies it, Decline discards
// it, Skip defers it to a "review skipped" round at the end. Everything is keyed by a stable id
// (the issue's index in a frozen working copy) so skip + index-shift can't corrupt targets.
// ---------------------------------------------------------------------------
export function useQaReview({ blocks, dna, chapters, onChaptersChange, trackId }) {
    const [validating, setValidating] = useState(false)
    const [error, setError] = useState("")
    const [qa, setQa] = useState(null)
    const [issues, setIssues] = useState([])          // frozen working copy of qa.issues (itemIndex remapped on shift)
    const [queue, setQueue] = useState([])            // ordered ids (indices into issues) that canApply
    const [cursor, setCursor] = useState(-1)          // position in queue of the active change; -1 = none/done
    const [status, setStatus] = useState({})          // id -> "pending" | "accepted" | "declined" | "skipped" | "stale"
    const [proposals, setProposals] = useState({})    // id -> { state: "loading"|"ready"|"error", revised?, diff?, error? }

    const activeId = cursor >= 0 && cursor < queue.length ? queue[cursor] : null

    // Latest values for async callbacks, so we never read a stale render closure.
    const chaptersRef = useRef(chapters); chaptersRef.current = chapters
    const onChangeRef = useRef(onChaptersChange); onChangeRef.current = onChaptersChange
    const blocksRef = useRef(blocks); blocksRef.current = blocks
    const dnaRef = useRef(dna); dnaRef.current = dna
    const issuesRef = useRef(issues); issuesRef.current = issues
    const statusRef = useRef(status); statusRef.current = status
    const proposalsRef = useRef(proposals); proposalsRef.current = proposals
    const queueRef = useRef(queue); queueRef.current = queue
    const cursorRef = useRef(cursor); cursorRef.current = cursor
    const activeIdRef = useRef(activeId); activeIdRef.current = activeId

    // --- Refresh-proof persistence (localStorage, keyed by trackId) ---
    const storageKey = trackId ? `mm:qa-review:${trackId}` : null
    const hydratedRef = useRef(false)

    // Restore a persisted review (report, queue, progress, ready proposals) after a reload. Runs once,
    // as soon as the trackId is known; until then the in-memory state stays empty.
    useEffect(() => {
        if (hydratedRef.current || !storageKey) return
        hydratedRef.current = true
        try {
            const raw = window.localStorage.getItem(storageKey)
            if (!raw) return
            const snap = JSON.parse(raw)
            if (!snap || !snap.qa) return
            setQa(snap.qa)
            setIssues(snap.issues ?? []); issuesRef.current = snap.issues ?? []
            setQueue(snap.queue ?? []); queueRef.current = snap.queue ?? []
            setStatus(snap.status ?? {}); statusRef.current = snap.status ?? {}
            setProposals(snap.proposals ?? {}); proposalsRef.current = snap.proposals ?? {}
            const c = typeof snap.cursor === "number" ? snap.cursor : -1
            setCursor(c); cursorRef.current = c
        } catch { /* ignore a corrupt cache */ }
    }, [storageKey])

    // Fetch (or compute) the proposal for one change. Removes are instant & client-side; add/edit ask
    // the server. Cached results make skip/revisit free. `force` re-fetches after an error.
    const fetchProposal = useCallback((id, force = false) => {
        const it = issuesRef.current[id]
        if (!it) return
        const op = it.operation ?? "edit"
        const chapter = chaptersRef.current[it.chapterIndex]
        const existing = proposalsRef.current[id]
        if (!force && existing && (existing.state === "ready" || existing.state === "loading")) return

        if (op === "remove") {
            setProposals(p => ({ ...p, [id]: { state: "ready", revised: {}, diff: buildDiff(it, chapter, {}) } }))
            return
        }

        setProposals(p => ({ ...p, [id]: { state: "loading" } }))
        const payload = {
            blocks: blocksRef.current,
            dna: dnaRef.current,
            chapter: {
                title: chapter?.title,
                theme: chapter?.theme,
                description: chapter?.description,
                activities: (chapter?.activities ?? []).map(a => ({ title: a.title, description: a.description })),
                sparks: (chapter?.sparks ?? []).map(s => ({ name: s.name || s.displayName, displayName: s.displayName, description: s.description, content: s.content })),
            },
            outline: outlineCtx(chaptersRef.current),
            // Omit itemIndex for "add" (no target) and chapter-level fixes; the server requires >= 0.
            issue: {
                area: it.area,
                itemType: it.itemType,
                operation: op,
                ...(op !== "add" && typeof it.itemIndex === "number" && it.itemIndex >= 0 ? { itemIndex: it.itemIndex } : {}),
                problem: it.problem,
                fix: it.fix,
            },
        }
        api.post("tracks/generate/revise", payload)
            .then(res => {
                // The change may have been resolved or shifted while the call was in flight — only cache
                // the preview if it is still an unresolved, current target.
                if (statusRef.current[id] !== "pending") return
                const revised = res.data ?? {}
                const ch = chaptersRef.current[issuesRef.current[id].chapterIndex]
                setProposals(p => ({ ...p, [id]: { state: "ready", revised, diff: buildDiff(issuesRef.current[id], ch, revised) } }))
            })
            .catch(e => {
                setProposals(p => ({ ...p, [id]: { state: "error", error: errMessage(e, "Couldn't generate this change. Please try again.") } }))
            })
    }, [])

    // Whenever a new change becomes active, make sure its proposal is ready.
    useEffect(() => {
        if (activeId == null) return
        fetchProposal(activeId)
    }, [activeId, fetchProposal])

    // "Validate track with AI": read-only QA review, then (re)build the queue.
    const validate = useCallback(() => {
        setError("")
        setQa(null)
        setIssues([]); setQueue([]); setStatus({}); setProposals({}); setCursor(-1)
        setValidating(true)
        api.post("tracks/generate/qa", { blocks: blocksRef.current, dna: dnaRef.current, chapters: chapterPayload(chaptersRef.current) })
            .then(res => {
                const report = res.data ?? null
                setQa(report)
                const iss = (report?.issues ?? []).map(x => ({ ...x }))
                const q = iss.map((_, i) => i).filter(i => canApply(iss[i], chaptersRef.current, !!onChangeRef.current))
                const st = {}
                q.forEach(id => { st[id] = "pending" })
                setIssues(iss); issuesRef.current = iss
                setQueue(q); queueRef.current = q
                setStatus(st); statusRef.current = st
                setProposals({}); proposalsRef.current = {}
                const start = q.length ? 0 : -1
                setCursor(start); cursorRef.current = start
                setValidating(false)
            })
            .catch(e => {
                setError(errMessage(e, "Couldn't validate the track. Please try again."))
                setValidating(false)
            })
    }, [])

    // Find the next still-"pending" position, scanning forward from `from` and wrapping once. Skipped
    // changes are NOT auto-revisited here — they resurface via reviewSkipped() at the end.
    const nextPending = (from, st) => {
        const q = queueRef.current
        const n = q.length
        if (n === 0) return -1
        for (let step = 1; step <= n; step++) {
            const pos = (from + step) % n
            if (st[q[pos]] === "pending") return pos
        }
        return -1
    }

    // Resolve the active change to a terminal/deferred status and advance the cursor.
    const resolve = (id, newStatus, extraStatus = {}) => {
        const st = { ...statusRef.current, [id]: newStatus, ...extraStatus }
        statusRef.current = st
        setStatus(st)
        const pos = nextPending(cursorRef.current, st)
        cursorRef.current = pos
        setCursor(pos)
    }

    // After an accepted remove, every UNRESOLVED issue in the same chapter+itemType with a higher
    // itemIndex shifts down by one; the one that pointed at the just-removed slot goes "stale".
    const applyRemoveShift = (removedId, ci, type, k) => {
        const iss = issuesRef.current
        const st0 = statusRef.current
        const unresolved = (s) => s === "pending" || s === "skipped"

        const newIssues = iss.map((x, xid) => {
            if (xid === removedId || !unresolved(st0[xid])) return x
            if (x.chapterIndex === ci && x.itemType === type && typeof x.itemIndex === "number" && x.itemIndex > k) {
                return { ...x, itemIndex: x.itemIndex - 1 }
            }
            return x
        })

        const newProps = { ...proposalsRef.current }
        const staleStatus = {}
        iss.forEach((x, xid) => {
            if (xid === removedId || !unresolved(st0[xid])) return
            if (x.chapterIndex === ci && x.itemType === type && typeof x.itemIndex === "number") {
                if (x.itemIndex > k) delete newProps[xid]               // re-fetch against fresh context
                else if (x.itemIndex === k) { delete newProps[xid]; staleStatus[xid] = "stale" } // target gone
            }
        })

        issuesRef.current = newIssues; setIssues(newIssues)
        proposalsRef.current = newProps; setProposals(newProps)
        resolve(removedId, "accepted", staleStatus)
    }

    const acceptActive = useCallback(() => {
        const id = activeIdRef.current
        if (id == null) return
        const it = issuesRef.current[id]
        const op = it.operation ?? "edit"
        const ci = it.chapterIndex
        setError("")

        // Guard against a snapshot that no longer matches the live track (e.g. an intervening remove).
        if (!canApply(it, chaptersRef.current, !!onChangeRef.current)) { resolve(id, "stale"); return }

        if (op === "remove") {
            onChangeRef.current(prev => prev.map((c, idx) => idx === ci ? mergeOperation(c, it, {}) : c))
            applyRemoveShift(id, ci, it.itemType, it.itemIndex)
            return
        }

        const prop = proposalsRef.current[id]
        if (!prop || prop.state !== "ready") return // proposal not generated yet (Accept is disabled in the UI)
        const revised = prop.revised ?? {}
        onChangeRef.current(prev => prev.map((c, idx) => idx === ci ? mergeOperation(c, it, revised) : c))
        resolve(id, "accepted")
    }, [])

    const declineActive = useCallback(() => {
        const id = activeIdRef.current
        if (id == null) return
        resolve(id, "declined")
    }, [])

    const skipActive = useCallback(() => {
        const id = activeIdRef.current
        if (id == null) return
        resolve(id, "skipped")
    }, [])

    const retryActive = useCallback(() => {
        const id = activeIdRef.current
        if (id == null) return
        const np = { ...proposalsRef.current }; delete np[id]
        proposalsRef.current = np; setProposals(np)
        fetchProposal(id, true)
    }, [fetchProposal])

    // Reopen all deferred (skipped) changes for a second pass.
    const reviewSkipped = useCallback(() => {
        const st = { ...statusRef.current }
        let first = -1
        queueRef.current.forEach((id, pos) => {
            if (st[id] === "skipped") { st[id] = "pending"; if (first < 0) first = pos }
        })
        statusRef.current = st; setStatus(st)
        cursorRef.current = first; setCursor(first)
    }, [])

    // Dismiss every remaining skipped change at once (stays in completion).
    const declineSkipped = useCallback(() => {
        const st = { ...statusRef.current }
        queueRef.current.forEach(id => { if (st[id] === "skipped") st[id] = "declined" })
        statusRef.current = st; setStatus(st)
    }, [])

    // Persist the review so a reload restores it. Only "ready" proposals are kept; transient
    // loading/error ones refetch when their change is active again. Held off until after hydration so
    // the initial empty state can't clobber a saved review.
    useEffect(() => {
        if (!storageKey || !hydratedRef.current) return
        try {
            if (qa == null) { window.localStorage.removeItem(storageKey); return }
            const readyProposals = {}
            Object.keys(proposals).forEach(id => { if (proposals[id]?.state === "ready") readyProposals[id] = proposals[id] })
            window.localStorage.setItem(storageKey, JSON.stringify({ qa, issues, queue, cursor, status, proposals: readyProposals }))
        } catch { /* quota or unavailable — non-fatal */ }
    }, [storageKey, qa, issues, queue, cursor, status, proposals])

    // --- Derived view model ---
    const activeIt = activeId != null ? issues[activeId] : null
    const active = activeIt ? {
        id: activeId,
        op: activeIt.operation ?? "edit",
        itemType: activeIt.itemType,
        chapterIndex: activeIt.chapterIndex,
        itemIndex: activeIt.itemIndex,
        area: activeIt.area,
        chapter: activeIt.chapter,
        problem: activeIt.problem,
        fix: activeIt.fix,
        label: opLabel(activeIt),
        badgeColor: opColor(activeIt.operation ?? "edit"),
        proposal: proposals[activeId] ?? { state: "loading" },
    } : null

    const counts = { accepted: 0, declined: 0, skipped: 0, stale: 0 }
    Object.values(status).forEach(s => { if (counts[s] != null) counts[s]++ })

    const queueSet = new Set(queue)
    const manualIssues = issues.filter((_, i) => !queueSet.has(i))

    const total = queue.length
    const reviewedCount = counts.accepted + counts.declined + counts.stale
    const done = qa != null && total > 0 && cursor < 0

    return {
        validate, validating, error, qa,
        hasReport: qa != null,
        active,
        accept: acceptActive,
        decline: declineActive,
        skip: skipActive,
        retry: retryActive,
        reviewSkipped,
        declineSkipped,
        total,
        counts,
        reviewedCount,
        skippedRemaining: counts.skipped,
        manualIssues,
        done,
    }
}
